import axios from 'axios';
import { API_BASE } from '../config';
import { isTokenExpiringSoon, calculateRefreshTime } from '../utils/tokenManager';

// Create axios instance with correct base URL
const api = axios.create({
  baseURL: `${API_BASE}/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let accessToken = localStorage.getItem('access_token');
let refreshToken = localStorage.getItem('refresh_token');
let tokenRefreshTimeout = null;
let lastRefreshTime = 0;
let inflightRefreshPromise = null; // Mutex: deduplicate concurrent refresh calls
const MIN_REFRESH_INTERVAL = 2 * 60 * 1000; // Minimum 2 minutes between refreshes

// Single-flight refresh: all callers share one in-flight request
const refreshTokenOnce = async () => {
  if (inflightRefreshPromise) return inflightRefreshPromise;

  accessToken = localStorage.getItem('access_token');
  refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token available');

  inflightRefreshPromise = axios.post('/api/v1/auth/refresh', {
    refresh_token: refreshToken
  }, {
    headers: { 'Content-Type': 'application/json' }
  }).then(response => {
    if (!response.data?.access_token) {
      throw new Error('Refresh response missing access_token');
    }
    accessToken = response.data.access_token;
    localStorage.setItem('access_token', accessToken);
    if (response.data.refresh_token) {
      refreshToken = response.data.refresh_token;
      localStorage.setItem('refresh_token', refreshToken);
    }
    lastRefreshTime = Date.now();
    scheduleNextRefresh();
    return response.data;
  }).finally(() => {
    inflightRefreshPromise = null;
  });

  return inflightRefreshPromise;
};

// Proactive refresh (called by timer)
let proactiveRefreshRetries = 0;
const MAX_PROACTIVE_RETRIES = 3;

const performTokenRefresh = async () => {
  const timeSinceLastRefresh = Date.now() - lastRefreshTime;
  if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
    const remainingTime = MIN_REFRESH_INTERVAL - timeSinceLastRefresh;
    tokenRefreshTimeout = setTimeout(() => {
      scheduleNextRefresh();
    }, remainingTime);
    return;
  }

  try {
    await refreshTokenOnce();
    proactiveRefreshRetries = 0;
  } catch (error) {
    proactiveRefreshRetries++;
    console.error(`Token refresh failed (${proactiveRefreshRetries}/${MAX_PROACTIVE_RETRIES}):`, error);
    if (proactiveRefreshRetries >= MAX_PROACTIVE_RETRIES) {
      proactiveRefreshRetries = 0;
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return;
    }
    tokenRefreshTimeout = setTimeout(() => {
      performTokenRefresh();
    }, 5 * 60 * 1000);
  }
};

// ✅ ADD: Schedule token refresh at calculated time
const scheduleNextRefresh = () => {
  // Clear existing timeout
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
    tokenRefreshTimeout = null;
  }

  // Sync tokens from localStorage
  accessToken = localStorage.getItem('access_token');
  refreshToken = localStorage.getItem('refresh_token');
  
  if (!accessToken || !refreshToken) {
    return;
  }

  // Refresh 5 minutes before expiration (tokens are now 30-minute lived)
  const refreshTime = calculateRefreshTime(accessToken, 5);
  
  if (refreshTime === null) {
    tokenRefreshTimeout = setTimeout(() => {
      if (isTokenExpiringSoon(accessToken)) {
        performTokenRefresh();
      } else {
        scheduleNextRefresh(); // Recalculate
      }
    }, 15 * 60 * 1000);
    return;
  }

  if (refreshTime === 0) {
    // ✅ FIX: Check minimum interval before immediate refresh
    const timeSinceLastRefresh = Date.now() - lastRefreshTime;
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
      // Too soon since last refresh, schedule for later
      const remainingTime = MIN_REFRESH_INTERVAL - timeSinceLastRefresh;
      tokenRefreshTimeout = setTimeout(() => {
        scheduleNextRefresh();
      }, remainingTime);
      return;
    }
    
    performTokenRefresh();
    return;
  }

  tokenRefreshTimeout = setTimeout(() => {
    performTokenRefresh();
  }, refreshTime);
};

// ✅ ADD: Stop token refresh timer
const stopTokenRefreshTimer = () => {
  if (tokenRefreshTimeout) {
    clearTimeout(tokenRefreshTimeout);
    tokenRefreshTimeout = null;
  }
};

// Re-check and refresh token when tab becomes visible again
// Browsers throttle/pause setTimeout in background tabs, so the proactive
// refresh timer may not fire. This ensures we refresh on tab focus.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    accessToken = localStorage.getItem('access_token');
    refreshToken = localStorage.getItem('refresh_token');
    if (!accessToken || !refreshToken) return;

    if (isTokenExpiringSoon(accessToken)) {
      performTokenRefresh();
    } else {
      // Reschedule since the old timer may have drifted or been throttled
      scheduleNextRefresh();
    }
  });
}

// Request interceptor to add auth header
api.interceptors.request.use(
  (config) => {
    // ✅ ADD: Always get latest token from localStorage in case it was updated elsewhere
    accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const currentRefresh = localStorage.getItem('refresh_token');
      if (currentRefresh) {
        try {
          await refreshTokenOnce();
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError.response?.status);
          if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
            clearTokens();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          } else {
            return Promise.reject(error);
          }
        }
      } else {
        clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    // Handle other errors - preserve the original error structure
    if (error.response) {
      // For HTTP errors, preserve the response data
      const enhancedError = new Error(error.response?.data?.detail || error.message || 'An error occurred');
      enhancedError.response = error.response;
      enhancedError.status = error.response.status;
      return Promise.reject(enhancedError);
    }
    
    // For network errors, return as-is
    return Promise.reject(error);
  }
);

// Auth helper functions
export const setTokens = (access, refresh) => {
  if (!access || typeof access !== 'string') throw new Error('Valid access token is required');
  if (!refresh || typeof refresh !== 'string') throw new Error('Valid refresh token is required');
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
  lastRefreshTime = Date.now();
  scheduleNextRefresh();
};

export const clearTokens = () => {
  stopTokenRefreshTimer();
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

// ===== AUTHENTICATION API =====

export const authApi = {
  async login(credentials) {
    const response = await api.post('/auth/login', credentials);
    const { access_token, refresh_token, user } = response.data;
    
    setTokens(access_token, refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data;
  },

  async refresh() {
    return refreshTokenOnce();
  },

  async logout(refreshToken) {
    try {
      await api.post('/auth/logout', { refresh_token: refreshToken });
    } finally {
      clearTokens();
    }
  }
};

// ===== TURNOVER-IT API =====

const getDownloadFilename = contentDisposition => {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = contentDisposition?.match(/filename="([^"]+)"/i)?.[1];
  let filename = quoted || 'turnover-it-cv.pdf';
  if (encoded) {
    try {
      filename = decodeURIComponent(encoded);
    } catch (_error) {
      filename = quoted || 'turnover-it-cv.pdf';
    }
  }
  const safeFilename = filename.split(/[\\/]/).pop().replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return safeFilename.toLowerCase().endsWith('.pdf') ? safeFilename : 'turnover-it-cv.pdf';
};

export const turnoverItApi = {
  async getSearchCriteria(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/turnover-it-search-criteria`);
    return response.data;
  },

  async searchLocations(needId, search) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/turnover-it-location-suggestions`, {
      params: { search },
    });
    return response.data;
  },

  async searchProfiles(needId, body) {
    if (!needId) throw new Error('needId is required');
    const response = await api.post(`/needs/${needId}/turnover-it-profile-searches`, body);
    return response.data;
  },

  async downloadProfileCv(needId, selectionToken) {
    if (!needId) throw new Error('needId is required');
    if (!selectionToken) throw new Error('selectionToken is required');
    const response = await api.post(
      `/needs/${needId}/turnover-it-cv-preview`,
      { selectionToken },
      { responseType: 'blob' },
    );
    return {
      blob: response.data,
      filename: getDownloadFilename(response.headers?.['content-disposition']),
    };
  },

  async importProfile(needId, selectionToken) {
    if (!needId) throw new Error('needId is required');
    if (!selectionToken) throw new Error('selectionToken is required');
    const response = await api.post(`/needs/${needId}/turnover-it-profiles`, { selectionToken });
    return { data: response.data, status: response.status };
  },
};

// ===== JOBCONNECT API =====

export const createJobConnectIdempotencyKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const idempotencyConfig = key => ({
  headers: { 'Idempotency-Key': key || createJobConnectIdempotencyKey() },
});

export const jobConnectApi = {
  async listAllPublications(params = {}) {
    const response = await api.get('/jobconnect-publications', { params });
    return response.data;
  },

  async listPublications(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jobconnect-publications`);
    return response.data;
  },

  async createPublication(needId, body, idempotencyKey) {
    if (!needId) throw new Error('needId is required');
    const response = await api.post(
      `/needs/${needId}/jobconnect-publications`, body, idempotencyConfig(idempotencyKey),
    );
    return response.data;
  },

  async replacePublication(needId, publicationId, body) {
    if (!needId) throw new Error('needId is required');
    if (!publicationId) throw new Error('publicationId is required');
    const response = await api.put(`/needs/${needId}/jobconnect-publications/${publicationId}`, body);
    return response.data;
  },

  async approvePublication(needId, publicationId, body, idempotencyKey) {
    if (!needId) throw new Error('needId is required');
    if (!publicationId) throw new Error('publicationId is required');
    const response = await api.post(
      `/needs/${needId}/jobconnect-publications/${publicationId}/approvals`,
      body,
      idempotencyConfig(idempotencyKey),
    );
    return response.data;
  },

  async synchronizePublication(needId, publicationId, body, idempotencyKey) {
    if (!needId) throw new Error('needId is required');
    if (!publicationId) throw new Error('publicationId is required');
    const response = await api.post(
      `/needs/${needId}/jobconnect-publications/${publicationId}/synchronizations`,
      body,
      idempotencyConfig(idempotencyKey),
    );
    return response.data;
  },

  async listSynchronizations(needId, publicationId, params = {}) {
    if (!needId) throw new Error('needId is required');
    if (!publicationId) throw new Error('publicationId is required');
    const response = await api.get(
      `/needs/${needId}/jobconnect-publications/${publicationId}/synchronizations`, { params },
    );
    return response.data;
  },

  async deletePublication(needId, publicationId, expectedVersion, idempotencyKey) {
    if (!needId) throw new Error('needId is required');
    if (!publicationId) throw new Error('publicationId is required');
    if (!expectedVersion) throw new Error('expectedVersion is required');
    const response = await api.delete(`/needs/${needId}/jobconnect-publications/${publicationId}`, {
      params: { expectedVersion },
      ...idempotencyConfig(idempotencyKey),
    });
    return response.data;
  },

  async searchSkills(query, page = 1) {
    if (!query) throw new Error('query is required');
    const response = await api.get('/jobconnect-skills', { params: { q: query, page } });
    return response.data;
  },

  async listApplicationReceipts(needId, params = {}) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jobconnect-application-receipts`, { params });
    return response.data;
  },

  async resolveApplicationIdentity(needId, receiptId, body) {
    if (!needId) throw new Error('needId is required');
    if (!receiptId) throw new Error('receiptId is required');
    const response = await api.post(
      `/needs/${needId}/jobconnect-application-receipts/${receiptId}/candidate-resolutions`, body,
    );
    return response.data;
  },

  async decideApplication(needId, receiptId, body, idempotencyKey) {
    if (!needId) throw new Error('needId is required');
    if (!receiptId) throw new Error('receiptId is required');
    const response = await api.post(
      `/needs/${needId}/jobconnect-application-receipts/${receiptId}/decisions`,
      body,
      idempotencyConfig(idempotencyKey),
    );
    return response.data;
  },
};

// ===== HANDOFF API =====

export const handoffApi = {
  /**
   * Create a handoff code for cross-frontend authentication
   * @param {Object} payload - Handoff creation payload
   * @param {string} [payload.audience] - Optional audience identifier
   * @param {string} [payload.destination_url] - Optional destination URL
   * @returns {Promise<{code: string, expires_in: number, audience?: string}>}
   */
  async createHandoffCode(payload = {}) {
    const response = await api.post('/handoffs/', payload);
    return response.data;
  },

  /**
   * Exchange a handoff code for authentication tokens
   * @param {string} code - The handoff code to exchange
   * @returns {Promise<{access_token: string, refresh_token: string, expires_in: number, user: Object}>}
   */
  async exchangeHandoffCode(code) {
    const response = await api.post('/handoffs/exchanges/', { code });
    return response.data;
  }
};

// ===== FLOWS ADMIN API =====

export const flowsAdminApi = {
  async listFlows() {
    const response = await api.get('/admin/flows');
    return response.data;
  },

  async getFlow(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.get(`/admin/flows/${flowId}`);
    return response.data;
  },

  async retryPoll(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.post(`/admin/flows/${flowId}/retries`);
    return response.data;
  },

  async retryClassify(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.post(`/admin/flows/${flowId}/retry-classify`);
    return response.data;
  },

  async retryApply(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.post(`/admin/flows/${flowId}/retry-apply`);
    return response.data;
  },

  async verifyStep(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.post(`/admin/flows/${flowId}/verifications`);
    return response.data;
  },

  async cancelFlow(flowId) {
    if (!flowId) throw new Error('flowId is required');
    const response = await api.post(`/admin/flows/${flowId}/cancellations`);
    return response.data;
  },

};

// ===== WORKFLOW REQUESTS API =====

export const workflowRequestsApi = {
  async listWorkflowRequests(params = {}) {
    const response = await api.get('/workflow-requests', { params });
    return response.data;
  },

  async getWorkflowRequest(id) {
    if (!id) throw new Error('id is required');
    const response = await api.get(`/workflow-requests/${id}`);
    return response.data;
  },

  async createWorkflowRequest(data) {
    const response = await api.post('/workflow-requests', data);
    return response.data;
  },

  async updateWorkflowRequest(id, updates) {
    if (!id) throw new Error('id is required');
    const response = await api.patch(`/workflow-requests/${id}`, updates);
    return response.data;
  },

  async deleteWorkflowRequest(id) {
    if (!id) throw new Error('id is required');
    await api.delete(`/workflow-requests/${id}`);
  },

  async triggerWorkflowRequest(id) {
    if (!id) throw new Error('id is required');
    const response = await api.post(`/workflow-requests/${id}/triggers`);
    return response.data;
  },

  async cancelWorkflowRequest(id) {
    if (!id) throw new Error('id is required');
    const response = await api.post(`/workflow-requests/${id}/cancellations`);
    return response.data;
  },

  async retryWorkflowRequest(id) {
    if (!id) throw new Error('id is required');
    const response = await api.post(`/workflow-requests/${id}/retries`);
    return response.data;
  }
};

// ===== USERS API =====

export const usersApi = {
  async getMe() {
    const response = await api.get('/users/me');
    return response.data;
  },

  async listUsers(params = {}) {
    const response = await api.get('/users', { params });
    return response.data;
  },

  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async getUser(userId) {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  async updateUser(userId, updateData) {
    const response = await api.patch(`/users/${userId}`, updateData);
    return response.data;
  },

  async deleteUser(userId) {
    await api.delete(`/users/${userId}`);
  },

  // Add this new method for fetching sales users (works for all authenticated users)
  async listSalesUsers(params = {}) {
    const response = await api.get('/users', { 
      params: { 
        ...params, 
        role: 'Sales',
        limit: 100 // Get all sales users
      } 
    });
    return response.data;
  },

};

// ===== NEEDS API ===== (FIXED VERSION)

// ✅ IMPROVED: Status mapping between frontend and API with bidirectional support
const STATUS_MAPPING = {
  // Frontend -> API
  'no-candidate': 'NO_CANDIDATE',
  'candidate-identified': 'CANDIDATE_IDENTIFIED',
  'candidate-sent': 'CANDIDATE_SENT',
  'candidate-interview': 'CANDIDATE_INTERVIEW',
  'go-nogo': 'GO_NOGO',
  'sold': 'SALE',
  'canceled': 'CANCELED',
  'deleted': 'DELETED',
  'frozen': 'FROZEN',        // ✅ ADD: Missing mapping for frozen
  'push': 'PUSH',           // ✅ ADD: Missing mapping for push
  // API -> Frontend
  'NO_CANDIDATE': 'no-candidate',
  'CANDIDATE_IDENTIFIED': 'candidate-identified',
  'CANDIDATE_SENT': 'candidate-sent',
  'CANDIDATE_INTERVIEW': 'candidate-interview',
  'GO_NOGO': 'go-nogo',
  'SALE': 'sold',
  'CANCELED': 'canceled',
  'DELETED': 'deleted',
  'FROZEN': 'frozen',       // ✅ ADD: Missing mapping for frozen
  'PUSH': 'push',           // ✅ ADD: Missing mapping for push
};

// Transform API Need to frontend format
const transformNeedFromApi = (apiNeed) => {
  if (!apiNeed) throw new Error('apiNeed is required');
  const transformed = {
    // Basic fields - direct mapping from API response
    id: apiNeed.id,
    title: apiNeed.title,
    client: apiNeed.client,
    priority: apiNeed.priority,
    status: STATUS_MAPPING[apiNeed.column] || apiNeed.column,
    column: apiNeed.column,
    progress: apiNeed.progressPct,
    progressPct: apiNeed.progressPct,
    description: apiNeed.description,
    category: apiNeed.category,
    quantity: apiNeed.quantity || 1,
    consultants: apiNeed.consultants || [],
    candidates: apiNeed.candidates || [],
    jobDescriptionFile: apiNeed.jobDescriptionFile,
    createdAt: apiNeed.createdAt,
    updatedAt: apiNeed.updatedAt,
    
    // Commercial fields
    tjm: apiNeed.tjm,
    
    // Salesforce integration fields
    opportunitySalesforceUrl: apiNeed.opportunitySalesforceUrl,
    opportunityCreatedAt: apiNeed.opportunityCreatedAt,
    missionSalesforceUrl: apiNeed.missionSalesforceUrl,
    missionCreatedAt: apiNeed.missionCreatedAt,
    propositionSalesforceId: apiNeed.propositionSalesforceId,
    propositionSalesforceUrl: apiNeed.propositionSalesforceUrl,
    propositionCreatedAt: apiNeed.propositionCreatedAt,
    
    // Order clients fields
    orderClients: apiNeed.orderClients || [],
    orderClientsEmail: apiNeed.orderClientsEmail || [],
    
    // S3 URLs
    jdS3Url: apiNeed.jdS3Url,
    jdAnalysisS3Url: apiNeed.jdAnalysisS3Url,
    
    // JD Analysis fields
    jdAnalysisStatus: apiNeed.jdAnalysisStatus,
    jdAnalysis: apiNeed.jdAnalysis,
    
    // Individual JD fields (camelCase for frontend)
    jdSeniority: apiNeed.jdSeniority,
    jdTeam: apiNeed.jdTeam,
    jdProjectName: apiNeed.jdProjectName,
    jdCompany: apiNeed.jdCompany,
    jdLocation: apiNeed.jdLocation,
    jdEducation: apiNeed.jdEducation,
    jdExperienceYears: apiNeed.jdExperienceYears,
    jdResponsibilities: apiNeed.jdResponsibilities || [],
    jdRequiredSkills: apiNeed.jdRequiredSkills || [],
    jdDesiredSkills: apiNeed.jdDesiredSkills || [],
    jdSoftSkills: apiNeed.jdSoftSkills || [],
  };

  return transformed;
};

// ✅ CRITICAL FIX: Export the function right after its definition
export { transformNeedFromApi };

// Transform frontend Need to API format
const transformNeedToApi = (frontendNeed) => {
  if (!frontendNeed) throw new Error('frontendNeed is required');
  const transformed = {
    title: frontendNeed.title,
    client: frontendNeed.client,
    priority: frontendNeed.priority,
    column: STATUS_MAPPING[frontendNeed.status] || frontendNeed.status,
    progressPct: frontendNeed.progress || frontendNeed.progressPct || 0,
    description: frontendNeed.description,
    category: frontendNeed.category,
    quantity: frontendNeed.quantity || 1,
    consultants: frontendNeed.consultants || [],
    candidates: frontendNeed.candidates || [],
    jobDescriptionFile: frontendNeed.jobDescriptionFile,
    orderClients: frontendNeed.orderClients || [],
    orderClientsEmail: frontendNeed.orderClientsEmail || [],
    tjm: frontendNeed.tjm,
    
    // ✅ FIX: Use the exact field names that match the backend schema aliases
    jdAnalysisStatus: frontendNeed.jdAnalysisStatus,
    jdAnalysis: frontendNeed.jdAnalysis,
    jdSeniority: frontendNeed.jdSeniority,
    jdTeam: frontendNeed.jdTeam,
    jdProjectName: frontendNeed.jdProjectName,
    jdCompany: frontendNeed.jdCompany,
    jdLocation: frontendNeed.jdLocation,
    jdResponsibilities: frontendNeed.jdResponsibilities,
    jdRequiredSkills: frontendNeed.jdRequiredSkills,
    jdDesiredSkills: frontendNeed.jdDesiredSkills,
    jdExperienceYears: frontendNeed.jdExperienceYears,
    jdEducation: frontendNeed.jdEducation,
    jdSoftSkills: frontendNeed.jdSoftSkills,
    jdS3Url: frontendNeed.jdS3Url,
    jdAnalysisS3Url: frontendNeed.jdAnalysisS3Url,
    opportunitySalesforceUrl: frontendNeed.opportunitySalesforceUrl,
    opportunityCreatedAt: frontendNeed.opportunityCreatedAt,
    missionSalesforceUrl: frontendNeed.missionSalesforceUrl,
    missionCreatedAt: frontendNeed.missionCreatedAt,
    propositionSalesforceId: frontendNeed.propositionSalesforceId,
    propositionSalesforceUrl: frontendNeed.propositionSalesforceUrl,
    propositionCreatedAt: frontendNeed.propositionCreatedAt
  };

  return transformed;
};

export const needsApi = {
  async listNeeds(params = {}) {
    const response = await api.get('/needs', { params });
    const transformedItems = response.data.items?.map(transformNeedFromApi) || [];
    return {
      ...response.data,
      items: transformedItems,
      total: response.data.total,
      itemsCount: transformedItems.length
    };
  },

  async listAllNeeds() {
    const limit = 100;
    const firstPage = await this.listNeeds({ limit, offset: 0 });
    const total = firstPage.total || firstPage.items.length;
    const offsets = [];
    for (let offset = limit; offset < total; offset += limit) {
      offsets.push(offset);
    }
    const remainingPages = await Promise.all(
      offsets.map(offset => this.listNeeds({ limit, offset })),
    );
    return {
      ...firstPage,
      items: [firstPage, ...remainingPages].flatMap(page => page.items),
      total,
      itemsCount: total,
    };
  },
  
  async getNeed(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}`);
    return transformNeedFromApi(response.data);
  },
  
  async createNeed(needData) {
    const apiData = transformNeedToApi(needData);
    const response = await api.post('/needs', apiData);
    return transformNeedFromApi(response.data);
  },
  
  async updateNeed(needId, updateData) {
    if (!needId) throw new Error('needId is required');
    // Clean the update data to only include valid fields for the backend
    const cleanedData = {};
      
      // Include null values for clearing fields, but exclude undefined
      Object.keys(updateData).forEach(key => {
        const value = updateData[key];
        if (value !== undefined) {
          cleanedData[key] = value;
        }
      });
      
      // Transform frontend field names to backend field names
      const backendData = {
        // Basic fields (keep as-is if they exist)
        ...(cleanedData.title !== undefined && { title: cleanedData.title }),
        ...(cleanedData.client !== undefined && { client: cleanedData.client }),
        ...(cleanedData.priority !== undefined && { priority: cleanedData.priority }),
        ...(cleanedData.description !== undefined && { description: cleanedData.description }),
        ...(cleanedData.category !== undefined && { category: cleanedData.category }),
        ...(cleanedData.quantity !== undefined && { quantity: cleanedData.quantity }),
        ...(cleanedData.consultants !== undefined && { consultants: cleanedData.consultants }),
        ...(cleanedData.tjm !== undefined && { tjm: cleanedData.tjm }),
        
        // Progress field transformation
        ...(cleanedData.progressPct !== undefined && { progressPct: cleanedData.progressPct }),
        
        // Job description fields (camelCase -> snake_case)
        ...(cleanedData.jobDescriptionFile !== undefined && { job_description_file: cleanedData.jobDescriptionFile }),
        
        // Order clients fields (camelCase -> snake_case)
        ...(cleanedData.orderClients !== undefined && { order_clients: cleanedData.orderClients }),
        ...(cleanedData.orderClientsEmail !== undefined && { order_clients_email: cleanedData.orderClientsEmail }),
        
        // JD Analysis fields (camelCase -> snake_case)
        ...(cleanedData.jdAnalysisStatus !== undefined && { jd_analysis_status: cleanedData.jdAnalysisStatus }),
        ...(cleanedData.jdAnalysis !== undefined && { jd_analysis: cleanedData.jdAnalysis }),
        
        // Individual JD fields (camelCase -> snake_case)
        ...(cleanedData.jdSeniority !== undefined && { jd_seniority: cleanedData.jdSeniority }),
        ...(cleanedData.jdTeam !== undefined && { jd_team: cleanedData.jdTeam }),
        ...(cleanedData.jdProjectName !== undefined && { jd_project_name: cleanedData.jdProjectName }),
        ...(cleanedData.jdCompany !== undefined && { jd_company: cleanedData.jdCompany }),
        ...(cleanedData.jdLocation !== undefined && { jd_location: cleanedData.jdLocation }),
        ...(cleanedData.jdResponsibilities !== undefined && { jd_responsibilities: cleanedData.jdResponsibilities }),
        ...(cleanedData.jdRequiredSkills !== undefined && { jd_required_skills: cleanedData.jdRequiredSkills }),
        ...(cleanedData.jdDesiredSkills !== undefined && { jd_desired_skills: cleanedData.jdDesiredSkills }),
        ...(cleanedData.jdExperienceYears !== undefined && { jd_experience_years: cleanedData.jdExperienceYears }),
        ...(cleanedData.jdEducation !== undefined && { jd_education: cleanedData.jdEducation }),
        ...(cleanedData.jdSoftSkills !== undefined && { jd_soft_skills: cleanedData.jdSoftSkills }),
        
        // Salesforce fields (camelCase -> snake_case)
        ...(cleanedData.opportunitySalesforceUrl !== undefined && { opportunity_salesforce_url: cleanedData.opportunitySalesforceUrl }),
        ...(cleanedData.opportunitySalesforceId !== undefined && { opportunity_salesforce_id: cleanedData.opportunitySalesforceId }),
        ...(cleanedData.opportunityCreatedAt !== undefined && { opportunity_created_at: cleanedData.opportunityCreatedAt }),
        ...(cleanedData.missionSalesforceUrl !== undefined && { mission_salesforce_url: cleanedData.missionSalesforceUrl }),
        ...(cleanedData.missionCreatedAt !== undefined && { mission_created_at: cleanedData.missionCreatedAt }),
        ...(cleanedData.propositionSalesforceId !== undefined && { proposition_salesforce_id: cleanedData.propositionSalesforceId }),
        ...(cleanedData.propositionSalesforceUrl !== undefined && { proposition_salesforce_url: cleanedData.propositionSalesforceUrl }),
        ...(cleanedData.propositionCreatedAt !== undefined && { proposition_created_at: cleanedData.propositionCreatedAt }),
      };
      
    const response = await api.patch(`/needs/${needId}`, backendData);
    return transformNeedFromApi(response.data);
  },

  async deleteNeed(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.patch(`/needs/${needId}/column`, {
      column: STATUS_MAPPING['deleted']
    });
    return transformNeedFromApi(response.data);
  },

  async hardDeleteNeed(needId) {
    if (!needId) throw new Error('needId is required');
    await api.delete(`/needs/${needId}`);
  },

  async moveNeedColumn(needId, newStatus) {
    if (!needId) throw new Error('needId is required');
    if (!newStatus) throw new Error('newStatus is required');
    
    // ✅ FIX: Don't apply STATUS_MAPPING if the value is already in backend format
    let apiColumn;
    
    // Check if newStatus is already in backend format (uppercase with underscores)
    const backendFormats = [
      'NO_CANDIDATE',
      'CANDIDATE_IDENTIFIED',
      'CANDIDATE_SENT',
      'CANDIDATE_INTERVIEW',
      'GO_NOGO',
      'SALE',
      'CANCELED',
      'DELETED',
      'FROZEN',
      'PUSH'
    ];
    
    if (backendFormats.includes(newStatus)) {
      // Already in backend format, use as-is
      apiColumn = newStatus;
    } else {
      // Convert from frontend format to backend format
      apiColumn = STATUS_MAPPING[newStatus] || newStatus;
    }
    
    const response = await api.patch(`/needs/${needId}/column`, {
      column: apiColumn
    });
    return transformNeedFromApi(response.data);
  },
  async getCandidateScore(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/needs/${needId}/candidates/${candidateId}/score`);
    return response.data;
  },

  async updateCandidateScore(needId, candidateId, scoreData) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.post(`/needs/${needId}/candidates/${candidateId}/score`, scoreData);
    return response.data;
  },

  async exportNeedsCSV(params = {}) {
    const response = await api.get('/exports/needs.csv', { 
      params,
      responseType: 'blob' // Important for file downloads
    });
    
    // Create blob and download file
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'besoins_export.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return response;
  },
};

// ===== CANDIDATES API =====

export const candidatesApi = {
  async listCandidates(params = {}) {
    const response = await api.get('/candidates', { params });
    return response.data;
  },

  async listNeedCandidates(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/candidates`);
    return response.data.map(candidate => ({
      ...candidate,
      cvUrl: candidate.cvUrl ?? candidate.cv_url,
      expectedTjm: candidate.expectedTjm ?? candidate.expected_tjm,
      salesforceId: candidate.salesforceId ?? candidate.salesforce_id,
      createdAt: candidate.createdAt ?? candidate.created_at,
      updatedAt: candidate.updatedAt ?? candidate.updated_at,
      relevanceScore: candidate.relevanceScore ?? candidate.relevance_score,
      scoreJustification: candidate.scoreJustification ?? candidate.score_justification,
      scoreJobId: candidate.scoreJobId ?? candidate.score_job_id,
      scoredAt: candidate.scoredAt ?? candidate.scored_at
    }));
  },

  async getCandidate(candidateId) {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/candidates/${candidateId}`);
    return {
      ...response.data,
      cvUrl: response.data.cvUrl ?? response.data.cv_url,
      expectedTjm: response.data.expectedTjm ?? response.data.expected_tjm,
      salesforceId: response.data.salesforceId ?? response.data.salesforce_id,
      createdAt: response.data.createdAt ?? response.data.created_at,
      updatedAt: response.data.updatedAt ?? response.data.updated_at,
      relevanceScore: response.data.relevanceScore ?? response.data.relevance_score,
      scoreJustification: response.data.scoreJustification ?? response.data.score_justification,
      scoreJobId: response.data.scoreJobId ?? response.data.score_job_id,
      scoredAt: response.data.scoredAt ?? response.data.scored_at
    };
  },

  async updateCandidate(candidateId, updateData) {
    if (!candidateId) throw new Error('candidateId is required');
    const backendData = {
      ...updateData,
      cv_url: updateData.cvUrl ?? updateData.cv_url,
      expected_tjm: updateData.expectedTjm ?? updateData.expected_tjm,
      salesforce_id: updateData.salesforceId ?? updateData.salesforce_id
    };
    const response = await api.patch(`/candidates/${candidateId}`, backendData);
    return response.data;
  },

  // CRUD operations
  async createCandidate(candidateData) {
    const response = await api.post('/candidates', candidateData);
    return response.data;
  },
  
  async deleteCandidate(candidateId) {
    if (!candidateId) throw new Error('candidateId is required');
    await api.delete(`/candidates/${candidateId}`);
  },

  async addCandidate(needId, candidateData) {
    if (!needId) throw new Error('needId is required');
    const response = await api.post(`/needs/${needId}/candidates`, candidateData);
    return { data: response.data, status: response.status };
  },

  async removeCandidate(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    await api.delete(`/needs/${needId}/candidates/${candidateId}`);
  },

  async attachCandidateToNeed(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.post(`/needs/${needId}/candidates/${candidateId}`);
    return response.data;
  },

  async updateCandidateAnalysis(candidateId, analysisData) {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.patch(`/candidates/${candidateId}/analysis`, analysisData);
    return response.data;
  },

  async getStepsForProcess(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/needs/${needId}/candidates/${candidateId}/steps`);
    return response.data;
  },

  async getLatestStep(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/needs/${needId}/candidates/${candidateId}/step`);
    return response.data;
  },

  async updateStep(stepId, updateData) {
    if (!stepId) throw new Error('stepId is required');
    const response = await api.patch(`/steps/${stepId}`, updateData);
    return response.data;
  },

  async createStep(needId, candidateId, stepData) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.post(`/needs/${needId}/candidates/${candidateId}/steps`, stepData);
    return response.data;
  },

  async getCandidatesBulk(needIds) {
    if (!Array.isArray(needIds) || needIds.length === 0) throw new Error('needIds must be a non-empty array');
    const response = await api.get(`/candidates/bulk?need_ids=${needIds.join(',')}`);
    return response.data;
  },

  async getAllCandidates(search = null) {
    const params = search ? { search } : {};
    const response = await api.get('/candidates/all', { params });
    return response.data;
  },

  async listNeedCandidatesWithStatus(needId) {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/candidates/by-need/${needId}/with-status`);
    return response.data;
  },
  bulkLoadCandidates: async (needIds) => {
    if (!Array.isArray(needIds) || needIds.length === 0) throw new Error('needIds must be a non-empty array');
    const response = await api.post('/needs/bulk/candidates', needIds);
    return response.data;
  },

  async deleteStep(stepId) {
    if (!stepId) throw new Error('stepId is required');
    const response = await api.delete(`/steps/${stepId}`);
    return response.data;
  },

  async getAllScoringData() {
    const response = await api.get('/candidates/scoring/all');
    return response.data;
  },

  async getScoringDataByNeed(needId) {
    if (!needId) throw new Error('needId is required');
    const limit = 100;
    const firstResponse = await api.get(`/candidates/scoring/need/${needId}`, {
      params: { limit, offset: 0 },
    });
    const total = firstResponse.data.totalCount ?? firstResponse.data.scoringData?.length ?? 0;
    const offsets = [];
    for (let offset = limit; offset < total; offset += limit) {
      offsets.push(offset);
    }
    const remainingResponses = await Promise.all(offsets.map(offset => api.get(
      `/candidates/scoring/need/${needId}`,
      { params: { limit, offset } },
    )));
    return {
      ...firstResponse.data,
      scoringData: [firstResponse, ...remainingResponses].flatMap(
        response => response.data.scoringData || [],
      ),
      totalCount: total,
    };
  },

  async getScoringDataByCandidate(candidateId) {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/candidates/scoring/candidate/${candidateId}`);
    return response.data;
  }
};

// ===== STEPS/TIMELINE API =====

export const stepsApi = {
  createStep: async (candidateId, needId, stepData) => {
    if (!candidateId) throw new Error('candidateId is required');
    if (!needId) throw new Error('needId is required');
    const response = await api.post('/steps', {
      candidateId,
      needId,
      type: stepData.type,
      date: stepData.date,
      comment: stepData.comment,
      detail: stepData.detail
    });
    return response.data;
  },

  getSteps: async (candidateId, needId) => {
    if (!candidateId) throw new Error('candidateId is required');
    if (!needId) throw new Error('needId is required');
    const response = await api.get('/steps', {
      params: { candidateId, needId }
    });
    return response.data;
  },

  updateStepDetails: async (stepId, detailData) => {
    if (!stepId) throw new Error('stepId is required');
    const response = await api.patch(`/steps/${stepId}/details`, detailData);
    return response.data;
  },
  getLatestStep: async (needId, candidateId) => {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/needs/${needId}/candidates/${candidateId}/step`);
    return response.data;
  },

  bulkLoadSteps: async (pairs) => {
    if (!Array.isArray(pairs)) throw new Error('pairs must be an array');
    const response = await api.post('/steps/bulk', pairs);
    return response.data;
  }
};

// ===== SETUP API =====

export const setupApi = {
  async setupAdmin(userData) {
    const response = await api.post('/setups/admin', userData);
    return response.data;
  }
};

// ===== UPLOADS API =====

export const uploadsApi = {
  // CV endpoints
  presignCv: async (candidateId, filename) => {
    if (!candidateId) throw new Error('candidateId is required');
    if (!filename) throw new Error('filename is required');
    const response = await api.get(`/candidates/${candidateId}/cv/presign?filename=${encodeURIComponent(filename)}&method=POST`);
    return response.data;
  },

  completeCv: async (candidateId, data) => {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.post(`/candidates/${candidateId}/cv/complete`, data);
    return response.data;
  },

  downloadCv: async (candidateId) => {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/candidates/${candidateId}/cv`);
    return response.data;
  },

  downloadCvFile: async (candidateId) => {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.get(`/candidates/${candidateId}/cv/file`, {
      responseType: 'blob'
    });
    return response.data;
  },

  deleteCv: async (candidateId) => {
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.delete(`/candidates/${candidateId}/cv`);
    return response.data;
  },

  // JD endpoints
  presignJD: async (needId, filename) => {
    if (!needId) throw new Error('needId is required');
    if (!filename) throw new Error('filename is required');
    const response = await api.get(`/needs/${needId}/jd/presign?filename=${encodeURIComponent(filename)}&method=POST`);
    return response.data;
  },

  completeJD: async (needId, data) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.post(`/needs/${needId}/jd/complete`, data);
    return response.data;
  },

  downloadJD: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jd`);
    return response.data;
  },

  downloadJDFile: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jd/file`, {
      responseType: 'blob'
    });
    return response.data;
  },

  deleteJD: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.delete(`/needs/${needId}/jd`);
    return response.data;
  },

  downloadJDAnalysis: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jd-analysis`);
    return response.data;
  },

  downloadJDAnalysisFile: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.get(`/needs/${needId}/jd-analysis/file`, {
      responseType: 'blob'
    });
    return response.data;
  },

  reanalyzeJD: async (needId) => {
    if (!needId) throw new Error('needId is required');
    const response = await api.post(`/needs/${needId}/jd/analysis`);
    return response.data;
  }
};

// ===== ANALYSIS API =====

export const analysisApi = {
  async scoreRelevance(needId, candidateId) {
    if (!needId) throw new Error('needId is required');
    if (!candidateId) throw new Error('candidateId is required');
    const response = await api.post('/analyses-ia/relevance-scores', {
      need_id: needId,
      candidate_id: candidateId
    });
    return response.data;
  }
};

// ===== MEETING MINUTES API =====

export const meetingMinutesApi = {
  async upload(filename) {
    if (!filename) throw new Error('filename is required');
    const response = await api.post('/meeting-minutes/upload', null, { params: { filename } });
    return response.data;
  },

  async generate(objectKey, meetingType, filename, sourceType = 'audio') {
    if (!objectKey) throw new Error('objectKey is required');
    if (!meetingType) throw new Error('meetingType is required');
    if (!filename) throw new Error('filename is required');
    const response = await api.post('/meeting-minutes/generate', {
      object_key: objectKey,
      meeting_type: meetingType,
      filename,
      source_type: sourceType,
    });
    return response.data;
  },

  async getStatus(jobId) {
    if (!jobId) throw new Error('jobId is required');
    const response = await api.get(`/meeting-minutes/${jobId}/status`);
    return response.data;
  },

  async getResult(jobId) {
    if (!jobId) throw new Error('jobId is required');
    const response = await api.get(`/meeting-minutes/${jobId}/result`);
    return response.data;
  },

  async uploadVideo(file) {
    if (!file) throw new Error('file is required');
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/meeting-minutes/upload-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  async uploadTranscript(file) {
    if (!file) throw new Error('file is required');
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/meeting-minutes/upload-transcript', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async getDocx(jobId) {
    if (!jobId) throw new Error('jobId is required');
    const response = await api.get(`/meeting-minutes/${jobId}/result/docx`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getContent(jobId) {
    if (!jobId) throw new Error('jobId is required');
    const response = await api.get(`/meeting-minutes/${jobId}/result/content`);
    return response.data;
  },
};

// ✅ ADD: Start timer if tokens exist on module load
if (accessToken && refreshToken) {
  scheduleNextRefresh();
}

// Export the main api instance for direct use if needed
export default api;
